import { httpEnvelope as http } from "./http";
import type { Project } from "./project.service";

export interface WizardQuestion {
  key: string;
  label: string;
  type: "text" | "textarea" | "boolean";
  required?: boolean;
  placeholder?: string;
  default?: boolean;
}

export type WizardAnswers = Record<string, string | boolean>;

export interface Prd {
  summary: string;
  entities: { name: string; description?: string; fields?: string[] }[];
  modules: { name: string; description?: string }[];
  profiles: { name: string; permissions?: string[] }[];
  flows: { name: string; steps?: string[] }[];
  non_functional: string[];
  suggested_template: string;
}

export interface Blueprint {
  uuid: string;
  title: string;
  status: "draft" | "ready" | "converted";
  answers: WizardAnswers;
  prd: Prd;
  suggested_template: string;
  generated_by_ai: boolean;
  project: string | null;
}

export type BacklogStatus =
  | "pending"
  | "ready"
  | "running"
  | "testing"
  | "completed"
  | "failed"
  | "blocked"
  | "skipped";

export interface BacklogItem {
  uuid: string;
  stage: string;
  stage_label: string;
  type: string;
  priority: number;
  dependencies: number[];
  ai_generated: boolean;
  position: number;
  title: string;
  description: string | null;
  instruction: string;
  status: BacklogStatus;
  result: string | null;
  attempts: number;
  completed_at: string | null;
  finished_at: string | null;
}

export interface BacklogProgress {
  total: number;
  done: number;
  stages: { stage: string; label: string; total: number; done: number; failed: number; blocked: number }[];
  items: BacklogItem[];
}

export interface AutonomyRun {
  activity?: { title: string; status: string; updated_at: string | null } | null;
  uuid: string;
  status: "queued" | "running" | "completed" | "halted" | "cancelled";
  halt_reason: string | null;
  halt_detail: string | null;
  max_items: number;
  processed: number;
  failed: number;
  cancel_requested: boolean;
  started_at: string | null;
  finished_at: string | null;
}

export interface AutonomyState {
  run: AutonomyRun | null;
  backlog: BacklogProgress;
}

/** Caminho "Criar sistema com IA": questionário → PRD → projeto → backlog. */
export const blueprintService = {
  async questions(): Promise<WizardQuestion[]> {
    const body = await http.get<{ data: WizardQuestion[] }>("/wizard/questions");
    return body.data;
  },

  async draft(answers: WizardAnswers): Promise<Blueprint> {
    const body = await http.post<{ data: Blueprint }>("/wizard/blueprints", { answers });
    return body.data;
  },

  async regenerate(uuid: string, answers: WizardAnswers): Promise<Blueprint> {
    const body = await http.put<{ data: Blueprint }>(`/wizard/blueprints/${uuid}`, { answers });
    return body.data;
  },

  async convert(uuid: string, name: string): Promise<{ project: Project; backlog: BacklogProgress }> {
    const body = await http.post<{ data: Project; backlog: BacklogProgress }>(
      `/wizard/blueprints/${uuid}/convert`,
      { name },
    );
    return { project: body.data, backlog: body.backlog };
  },

  async backlog(projectId: string): Promise<BacklogProgress> {
    const body = await http.get<{ data: BacklogProgress }>(`/projects/${projectId}/backlog`);
    return body.data;
  },

  async runNext(projectId: string, item?: string): Promise<BacklogProgress> {
    const body = await http.post<{ data: BacklogProgress }>(
      `/projects/${projectId}/backlog/run`,
      item ? { item } : {},
    );
    return body.data;
  },

  async skip(projectId: string, item: string): Promise<BacklogProgress> {
    const body = await http.post<{ data: BacklogProgress }>(
      `/projects/${projectId}/backlog/${item}/skip`,
      {},
    );
    return body.data;
  },

  /** Alterações do usuário na tarefa: prioridade, texto ou reabertura. */
  async updateItem(
    projectId: string,
    item: string,
    changes: { priority?: number; title?: string; description?: string; instruction?: string; reopen?: boolean },
  ): Promise<BacklogProgress> {
    const body = await http.patch<{ data: BacklogProgress }>(
      `/projects/${projectId}/backlog/${item}`,
      changes,
    );
    return body.data;
  },

  async autonomy(projectId: string): Promise<AutonomyState> {
    const body = await http.get<{ data: AutonomyState }>(`/projects/${projectId}/autonomy`);
    return body.data;
  },

  async startAutonomy(projectId: string, maxItems?: number, recovery: { resume?: boolean; item?: string } = {}): Promise<AutonomyState> {
    const body = await http.post<{ data: AutonomyState }>(
      `/projects/${projectId}/autonomy`,
      { ...(maxItems ? { max_items: maxItems } : {}), ...recovery },
    );
    return body.data;
  },

  async cancelAutonomy(projectId: string): Promise<AutonomyState> {
    const body = await http.post<{ data: AutonomyState }>(`/projects/${projectId}/autonomy/cancel`, {});
    return body.data;
  },
};
